import { useEffect, useState } from "react";
import { Share2, Flame } from "lucide-react";
import { LINKS, IMAGES } from "@/lib/constants";
import ShareModal from "./ShareModal";
import { TM } from "./TM";

export const Footer = () => {
  const [shareOpen, setShareOpen] = useState(false);
  const [shareCount, setShareCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const base = process.env.REACT_APP_BACKEND_URL;
    if (!base) return;
    fetch(`${base}/api/share/today-count`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!cancelled && data && typeof data.count === "number") {
          setShareCount(data.count);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [shareOpen]);

  return (
    <footer
      data-testid="vault-footer"
      className="relative border-t border-[rgba(201,162,91,0.18)] pt-16 pb-10"
    >
      <div className="max-w-7xl mx-auto px-6">
        {/* Top row */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-10">
          <div className="max-w-xl">
            <div className="flex items-center gap-3">
              <span className="brand-seal w-12 h-12 overflow-hidden">
                <img
                  src={IMAGES.brandLogo}
              alt="SHYFT Coalition™"
                  className="w-11 h-11 object-contain rounded-full"
                />
              </span>
              <span className="font-serif-display text-2xl text-white tracking-wider">
                SHYFT<span className="text-gold ml-1.5">Coalition<TM /></span>
              </span>
            </div>
            <p className="mt-5 text-[#b8b3aa] leading-relaxed text-sm md:text-base">
              The SHYFT Coalition<TM />. A private network for people who believe
              in participating in their own rescue.
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <a
              href={LINKS.community}
              target="_blank"
              rel="noreferrer"
              data-testid="footer-join-cta"
              className="btn-gold !py-2 !px-5 !text-xs"
            >
              Join Our Community
            </a>
            <a
              href={LINKS.appSubscription}
              target="_blank"
              rel="noreferrer"
              data-testid="footer-app-cta"
              className="btn-outline-gold !py-2 !px-5 !text-xs"
            >
              Activate the App
            </a>
            <div className="relative flex flex-col items-end">
              {shareCount >= 3 ? (
                <span
                  data-testid="share-social-proof"
                  className="mb-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-[rgba(30,144,255,0.45)] bg-[#0b1422]/70 backdrop-blur text-[10px] tracking-[0.16em] uppercase font-semibold text-[#6fb3ff] animate-pulse"
                >
                  <Flame size={11} className="text-[#1e90ff]" />
                  {shareCount} shared today
                </span>
              ) : null}
              <button
                type="button"
                onClick={() => setShareOpen(true)}
                data-testid="footer-share-cta"
                className="btn-outline-gold !py-2 !px-5 !text-xs"
              >
                <Share2 size={12} strokeWidth={2.2} />
                Share
              </button>
            </div>
          </div>
        </div>

        <div className="vault-divider my-12" />

        {/* Link columns */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-10">
          <div>
            <div className="eyebrow">Ecosystem</div>
            <ul className="mt-5 space-y-3">
              <li>
                <a
                  href={LINKS.community}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="footer-link-community"
                  className="text-[#e7e1d4] hover:text-[#e7c98a] transition text-sm"
                >
                  Our Community
                </a>
              </li>
              <li>
                <a
                  href={LINKS.appSubscription}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="footer-link-app"
                  className="text-[#e7e1d4] hover:text-[#e7c98a] transition text-sm"
                >
                  SHYFT App
                </a>
              </li>
              <li>
                <a
                  href={LINKS.indicators}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="footer-link-indicators"
                  className="text-[#e7e1d4] hover:text-[#e7c98a] transition text-sm"
                >
                  SHYFT ORB
                </a>
              </li>
              <li>
                <a
                  href={LINKS.smartlock}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="footer-link-smartlock"
                  className="text-[#e7e1d4] hover:text-[#e7c98a] transition text-sm"
                >
                  SmartLock&trade;
                </a>
              </li>
              <li>
                <a
                  href={LINKS.eazybot}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="footer-link-eazybot"
                  className="text-[#e7e1d4] hover:text-[#e7c98a] transition text-sm"
                >
                  EazyBot
                </a>
              </li>
              <li>
                <a
                  href={LINKS.shyftClasses}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="footer-link-classes"
                  className="text-[#e7e1d4] hover:text-[#e7c98a] transition text-sm"
                >
                  Class Schedule
                </a>
              </li>
            </ul>
          </div>

          <div>
            <div className="eyebrow">Storefront</div>
            <ul className="mt-5 space-y-3">
              <li>
                <a
                  href={LINKS.indicators}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="footer-link-indicator-licenses"
                  className="text-[#e7e1d4] hover:text-[#e7c98a] transition text-sm"
                >
                  Indicator Licenses
                </a>
              </li>
              <li>
                <a
                  href={LINKS.apparel}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="footer-link-apparel"
                  className="text-[#e7e1d4] hover:text-[#e7c98a] transition text-sm"
                >
                  Apparel
                </a>
              </li>
              <li>
                <a
                  href={LINKS.books}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="footer-link-books"
                  className="text-[#e7e1d4] hover:text-[#e7c98a] transition text-sm"
                >
                  Print Publications
                </a>
              </li>
              <li>
                <a
                  href={LINKS.store}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="footer-link-store"
                  className="text-[#e7e1d4] hover:text-[#e7c98a] transition text-sm"
                >
                  Full Storefront
                </a>
              </li>
            </ul>
          </div>

          <div>
            <div className="eyebrow">Legal</div>
            <ul className="mt-5 space-y-3">
              <li>
                <a
                  href={LINKS.privacy}
                  data-testid="footer-link-privacy"
                  className="text-[#e7e1d4] hover:text-[#e7c98a] transition text-sm"
                >
                  Privacy Policy
                </a>
              </li>
              <li>
                <a
                  href={LINKS.terms}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="footer-link-terms"
                  className="text-[#e7e1d4] hover:text-[#e7c98a] transition text-sm"
                >
                  Terms of Service
                </a>
              </li>
              <li>
                <a
                  href={LINKS.risk}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="footer-link-risk"
                  className="text-[#e7e1d4] hover:text-[#e7c98a] transition text-sm"
                >
                  Risk Disclaimer
                </a>
              </li>
              <li>
                <a
                  href={LINKS.refund}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="footer-link-refund"
                  className="text-[#e7e1d4] hover:text-[#e7c98a] transition text-sm"
                >
                  Refund &amp; Cancellation Policy
                </a>
              </li>
            </ul>
          </div>

          <div>
            <div className="eyebrow">Support</div>
            <ul className="mt-5 space-y-3">
              <li>
                <a
                  href={`mailto:${LINKS.supportEmail}`}
                  data-testid="footer-support-email"
                  className="text-[#e7c98a] hover:text-white transition text-sm"
                >
                  {LINKS.supportEmail}
                </a>
              </li>
              <li>
                <span className="text-[#b8b3aa] text-xs leading-relaxed block">
                  All inquiries route through email. No phone support.
                </span>
              </li>
            </ul>
          </div>
        </div>

        <div className="vault-divider my-10" />

        <div className="flex flex-col md:flex-row justify-between items-center gap-3 text-xs text-[#80786c]">
          <span data-testid="footer-copyright">
            © {new Date().getFullYear()} SHYFT Coalition<TM />. All rights reserved.
          </span>
          <span className="eyebrow !text-[10px] !text-[#80786c]">
            The SHYFT Coalition<TM /> · Vault Edition
          </span>
        </div>
      </div>
      <ShareModal open={shareOpen} onClose={() => setShareOpen(false)} />
    </footer>
  );
};

export default Footer;
